/**
 * Dimension adapters — central registry for non-trivial dim
 * implementations.
 *
 * Built-in analytics dims (agents, endpoints, clusters, sessions,
 * tools, models) go through DIM_INFO's standard field/IN path in
 * analyticsClient.js. They don't need adapters.
 *
 * Anything else — synthetic dims (mcp_server, signal), external dims
 * (linear), or future user-supplied dims — registers a `DimAdapter`
 * here. The adapter exposes:
 *
 *   • A predicate that composes with the PROMPT-rooted analytics-api
 *     graph (so the dim participates in scope filtering, flow links,
 *     contrast, swimlane, getTraces, etc.) — null when the dim can't
 *     be expressed as a predicate at all (Linear-style external).
 *
 *   • Entry/count/sparkline pipelines that the sidebar and column
 *     view dispatch to without per-dim branching.
 *
 *   • Capability flags that drive the generic dispatch in
 *     analyticsClient.js + apiClient.js without scattering
 *     `if (dim === "mcp_server")` checks across the codebase.
 *
 * Adding a new synthetic dim is one file: define an adapter, register
 * it here. Compare to the pre-refactor state where each new dim
 * required edits in ~10 places (DIM_INFO, DIM_SINGULAR, three
 * dispatch sites in apiClient, one in analyticsClient, a label-
 * resolver branch in dimensionDefs, ANALYTICS_SUPPORTED_DIM_PLURALS,
 * and a hand-written executeXDimensionEntries).
 *
 *
 * ── DimAdapter interface ──────────────────────────────────────────
 *
 * {
 *   // ── Identity ───────────────────────────────────────────────
 *   id:           string,   // singular dim key, matches DIM_INFO   ("mcp_server")
 *   plural:       string,   // sidebar key, matches DIM_SINGULAR    ("mcp_servers")
 *   label:        string,   // human display                        ("MCP servers")
 *   description?: string,   // optional tooltip / docs
 *
 *   // ── Analytics-api composition ──────────────────────────────
 *   // Emit a FilterExpr fragment that AND-composes into a PROMPT-
 *   // rooted scope filter. Multi-id within the dim is OR semantics
 *   // (matches built-in dims' IN-list behavior). Return null when
 *   // the dim cannot be expressed as a predicate at all (the dim
 *   // is responsible for any translation in its own pipelines).
 *   predicate(ids, stepAlias = "p"): FilterExpr | null,
 *
 *   // Optional: dims whose JOIN steps must be added to the PROMPT
 *   // root when this adapter's predicate is in use. Required if
 *   // the predicate references a non-PROMPT alias — e.g. an
 *   // `ec.*` cluster field needs "cluster" here so
 *   // buildPromptRootAndFilter adds the PROMPT_HAS_EVENT_CLUSTER
 *   // join and the `ec` alias exists in the spec. Today's adapters
 *   // (flag, mcp_server) reference only `p.*` fields so they omit
 *   // this; future adapters touching joined entities must declare.
 *   predicateExtraDims?: string[],
 *
 *   // ── Scope-step rewriting (optional, for derived attribution) ──
 *   // Adapters whose semantics decompose into existing standard
 *   // scope steps (endpoint, time, session, …) implement
 *   // rewriteScopeStep instead of (or in addition to) predicate.
 *   // Typical case: a Linear ticket → endpoints assigned to ticket's
 *   // user × time window of the assignment. GitHub PR → endpoints
 *   // × commit-range window. Customer → endpoint set.
 *   //
 *   // toApiScope consults rewriteScopeStep BEFORE dispatch. If the
 *   // adapter returns a non-null array, that array replaces the
 *   // original step in the API scope; downstream code (buildPrompt-
 *   // RootAndFilter, getTraces, runAnalyticsFlowPair, etc.) sees
 *   // only standard steps and behaves uniformly.
 *   //
 *   // SYNC. Reads from the adapter's hydration cache. Return null
 *   // (or omit) to fall through to the predicate path. Return []
 *   // to drop the step from scope entirely — used when the step is
 *   // present but not yet hydrated; the scope shrinks until
 *   // hydration completes and a re-render picks up the resolution.
 *   rewriteScopeStep?(step, { range }): ApiScopeStep[] | null,
 *
 *   // ASYNC. Triggers off-band resolution (MCP call, external API
 *   // fetch, etc.) and populates the adapter's internal cache so
 *   // rewriteScopeStep can return synchronously on subsequent calls.
 *   // useScopeHydration (mounted at App level) fires this once per
 *   // (step.id, range) the moment the step appears in scope.
 *   //
 *   // The adapter is responsible for its own caching + invalidation
 *   // policy (range change usually invalidates; tenant change as
 *   // well). Adapters that supply rewriteScopeStep but not
 *   // hydrateScopeStep are presumed to resolve synchronously from
 *   // local data — fine for static rewrites; required for any
 *   // external resolution.
 *   hydrateScopeStep?({ step, range }): Promise<void>,
 *
 *   // SYNC, optional. Reports whether hydrateScopeStep's resolution has
 *   // landed for this (step, range). Returns false while pending — when
 *   // rewriteScopeStep would still drop the step and a scope-dependent
 *   // query would run unscoped. The query hooks gate on this (via
 *   // scopePathAwaitingHydration) to hold in a loading state instead of
 *   // firing the unscoped request. Adapters with hydrateScopeStep but no
 *   // isScopeStepHydrated are treated as always-ready (legacy behavior:
 *   // the brief unscoped flash on first selection).
 *   isScopeStepHydrated?(step, { range }): boolean,
 *
 *   // ── Trace-detail chip extraction (optional) ────────────────
 *   // Pull adapter-specific facets out of a loaded trace's payload —
 *   // the redaction-type markers in trace.summary, the MCP servers
 *   // invoked in trace.tools, future skill names from parsed turns,
 *   // etc. ContextPanel collects from every registered adapter and
 *   // renders one group per adapter that returns a non-empty list.
 *   //
 *   // Return an array of facets; each carries:
 *   //   type    — adapter-internal id (stable across renders)
 *   //   label   — display
 *   //   count   — occurrences in this trace
 *   //   tooltip — hover text
 *   extractTraceFacets?(trace): Array<{ type, label, count, tooltip }>,
 *
 *   // Optional override of the group's eyebrow label in the trace
 *   // detail panel. Defaults to adapter.label.
 *   traceFacetGroupLabel?: string,
 *
 *   // ── Entries / count pipelines ──────────────────────────────
 *   // Both return data already shaped by shapeDimensionEntries
 *   // (see below). apiScope is post-toApiScope (singular dim keys).
 *   listEntries({ apiScope, offset, limit, search, sort, range }): { entries, total, maxValue, count },
 *   count({ apiScope, range }): number,
 *
 *   // Per-entry sparkline (optional). When omitted, the generic
 *   // path in apiClient.getDimEntrySparkline composes the adapter's
 *   // predicate with a bucketed COUNT — fine for any adapter where
 *   // composesWithPromptGraph is true.
 *   fetchSparkline?({ id, apiScope, range, bucketCount }): { series: number[] },
 *
 *   // ── Labels & render facets ─────────────────────────────────
 *   // Sync where possible. Async only when an external lookup is
 *   // required (Linear today). The label cache in dimensionDefs.js
 *   // accommodates both via fetchLabels.
 *   labelFor(id): string,
 *   fetchLabels?(ids): { [id]: string | { label, meta } },
 *
 *   // Optional UI hints — moved off _ALL_DIMENSIONS as adapters
 *   // migrate. None of these are required.
 *   renderIcon?(id, meta?):      { viewBox, paths } | null,
 *   renderTone?(id, meta?):      string | null,
 *   renderSecondary?(id, meta?): string | null,
 *
 *   // ── Flow-link participation (optional overrides) ──────────
 *   // Both methods have default implementations in dimAdapters.js
 *   // that any adapter with composesWithPromptGraph: true gets bound
 *   // for free at registry time. Adapters override only when their
 *   // flow semantics aren't "predicate + standard PROMPT query."
 *
 *   // Used when the OTHER axis is a real proto field — fan out one
 *   // query per id of THIS axis, filter = predicate(id) ∧ otherRef
 *   // IN otherIds, group by otherRef. Returns the per-id rows.
 *   fanOutForFlow?({ id, otherRef, otherIds, otherGroups, otherDimKey, range, baseScope = [] }):
 *     Promise<Array<{ otherId, count }>>,
 *
 *   // Used when both axes are synthetic (no field to group by on
 *   // either side). Returns the Set of logical prompt IDs matching
 *   // predicate(id); the dispatcher intersects sets in JS.
 *   // Default caps at 10K rows + memoizes by (adapter, id, range).
 *   fetchPromptIds?({ id, range, baseScope }): Promise<Set<string>>,
 *
 *   // ── Capability flags ───────────────────────────────────────
 *   // Drive generic dispatch in analyticsClient + apiClient without
 *   // per-dim branching. Each flag replaces a specific hard-coded
 *   // branch the pre-refactor code carried.
 *
 *   // True when the predicate emits a valid analytics-api expression
 *   // that the planner can run. Drives:
 *   //  - participation in runAnalyticsFlowPair fan-out (the default
 *   //    fanOutForFlow / fetchPromptIds methods get bound at
 *   //    registry time when this is true)
 *   //  - getDimEntrySparkline generic dispatch
 *   //  - eligibility to be a scope step in any PROMPT-rooted query
 *   composesWithPromptGraph: boolean,
 *
 *   // False → getTraces drops PROMPT_ENRICHED_PROMPT from the
 *   // projection and marks rows summaryUnavailable. The trace
 *   // detail panel still fetches the body via getTrace on click.
 *   // No adapter currently sets this to false; it exists for dims
 *   // whose bodies blow the gRPC 4 MB ceiling inlined in a list page.
 *   bodyFitsInListProjection: boolean,
 *
 *   // True → count can join the batched COUNT_DISTINCT spec for
 *   // multiple dims in one round trip. False for adapters whose
 *   // count() involves a bespoke pipeline; those land in the
 *   // countIndividually fallback bucket. Default: false.
 *   countBatchable?: boolean,
 * }
 */

import { mcpServerAdapter }  from "./mcpServers.js";
import { skillAdapter }      from "./skills.js";
import { memoryAdapter }     from "./memories.js";
import { signalAdapter }     from "./signalsDim.js";
import {
    buildPromptRootAndFilter,
    dimFieldRef,
    rangeToTimeRange,
    rowsToObjects,
    runQuery as runAnalyticsQuery,
} from "./analyticsClient.js";
import { LEAF_ROLLUP_ROW_LIMIT, leafToVisibleNodes, rollUpLeafCounts } from "./clusterLeafRollup.js";
import { loadClusterTree } from "./clusterTree.js";
import { countPromptGroups } from "./logicalPromptGroups.js";
import { promptCount } from "./promptMetrics.js";
import { toApiRange } from "./shell.js";
import { getAuthIdentityEpoch, onAuthIdentityChange } from "./authTokenStore.js";

// ── Flow-link participation defaults ───────────────────────────────
//
// runAnalyticsFlowPair dispatches to adapter.fanOutForFlow (one axis
// synthetic) or adapter.fetchPromptIds (both axes synthetic). Both
// have default implementations here that cover any adapter declaring
// composesWithPromptGraph: true. Adapters opt out by setting that
// flag false; adapters with bespoke flow semantics can override one
// or both methods to replace the default.
//
// Default implementations live as bare functions taking the adapter
// + args; they're bound onto each adapter at registry time below so
// the dispatcher can just call `adapter.fanOutForFlow(args)` without
// caring whether the adapter overrode it.

// fanOutForFlow — one query per synthetic id. Filter is
// `adapter.predicate([id]) AND otherRef IN otherIds`; group by
// otherRef; count prompts. Returns [{otherId, count}, …].
//
// `extraDims: [otherDimKey]` is the load-bearing bit — without it
// buildPromptRootAndFilter won't add the ILO_EVENT_CLUSTER child join
// step, and a fanOut where the OTHER axis is cluster (alias `ec`)
// fails with "validation: unknown step alias" at the gateway. (A
// model axis rides PROMPT's own column via promptRootDimRef — no
// join needed.)
async function defaultFanOutForFlow(adapter, { id, otherRef, otherIds, otherGroups, otherDimKey, range, baseScope = [] }) {
    const pred = adapter.predicate([id], "p");
    if (!pred || !otherRef) return [];
    const apiRange = toApiRange(range);
    const cast = (v) => (otherDimKey === "cluster" ? Number(v) : String(v));
    // Joins needed for the spec: the OTHER axis's join (so its
    // alias resolves) plus any joins the adapter's own predicate
    // depends on (declared via predicateExtraDims).
    const extraDims = [otherDimKey, ...(adapter.predicateExtraDims ?? [])];
    const { root, filter: scopeFilter, unsupported = [] } = buildPromptRootAndFilter({ scopePath: baseScope, extraDims });
    if (unsupported.length) return [];
    const spec = {
        root,
        timeRange: rangeToTimeRange(apiRange),
        filter: {
            andExpr: { children: [
                ...[scopeFilter].filter(Boolean),
                pred,
                { pred: { field: otherRef, op: "OPERATOR_IN", value: otherIds.map(cast) } },
            ]},
        },
        output: {
            dimensions: [{ field: otherRef, outputAlias: "other_id" }],
            metrics: [promptCount("n")],
        },
    };
    try {
        if (otherGroups) {
            // Count the entire parent predicate before reducing rows. Summing
            // leaf counts would count cross-leaf captures more than once.
            const rows = await countPromptGroups({
                root, timeRange: spec.timeRange,
                filter: { andExpr: { children: [scopeFilter, pred].filter(Boolean) } },
                groups: otherGroups, valueAlias: "n",
                runQuery: runAnalyticsQuery, rowsToObjects,
            });
            return rows.map((row) => ({ otherId: row.id, count: row.n }));
        }
        const rows = rowsToObjects(await runAnalyticsQuery(spec));
        return rows.map((r) => ({ otherId: r.other_id, count: Number(r.n) || 0 }));
    } catch (err) {
        console.warn(`[flow] fanOutForFlow failed for ${adapter.id}=${id}:`, err);
        return [];
    }
}

// flowCounts — every synthetic id's links to the other axis from one read,
// instead of fanOutForFlow's read per id. Two shapes, both exact:
//   - `flowGroup` adapters (predicate is "the joined field is one of ids"):
//     group by that field, which yields each id's rows as the per-id read would.
//   - `predicateIsRowLocal` adapters: one filtered count per id, on `p`.
// A Topics other axis is rolled up from leaves (see rollUpLeafCounts). Returns
// [{ synthId, otherId, count }], or null when the adapter supports neither
// shape or a rollup can't be exact, so the caller reads per id.
const FLOW_METRICS_PER_QUERY = 125; // plus the leaf and other-axis columns: the output-column cap

async function defaultFlowCounts(adapter, { ids, otherRef, otherIds, otherDimKey, range, baseScope = [] }) {
    const group = adapter.flowGroup;
    if (!group && !adapter.predicateIsRowLocal) return null;
    if (!otherRef || !ids.length || !otherIds.length) return [];
    const timeRange = rangeToTimeRange(toApiRange(range));
    const otherIsCluster = otherDimKey === "cluster";
    const extraDims = [otherDimKey, ...(adapter.predicateExtraDims ?? [])];
    const { root, filter: scopeFilter, unsupported = [] } = buildPromptRootAndFilter({
        scopePath: otherIsCluster ? [...baseScope, { dim: "cluster", ids: otherIds }] : baseScope,
        extraDims,
    });
    if (unsupported.length) return [];
    const leafToVisible = otherIsCluster ? leafToVisibleNodes(await loadClusterTree(), otherIds) : null;
    const chunks = [];
    for (let start = 0; start < ids.length; start += group ? ids.length : FLOW_METRICS_PER_QUERY) {
        chunks.push(ids.slice(start, start + (group ? ids.length : FLOW_METRICS_PER_QUERY)));
    }
    const results = await Promise.all(chunks.map(async (chunk) => {
        const filter = { andExpr: { children: [
            scopeFilter,
            adapter.predicate(chunk, "p"),
            otherIsCluster ? null : { pred: { field: otherRef, op: "OPERATOR_IN", value: otherIds.map(String) } },
        ].filter(Boolean) } };
        const metrics = group
            ? [{ alias: "n" }]
            : chunk.map((id, index) => ({ alias: `id_${index}`, predicate: adapter.predicate([id], "p") }));
        const groupDims = group ? [{ field: group.field, outputAlias: "synth" }] : [];
        let rows;
        if (otherIsCluster) {
            rows = await rollUpLeafCounts({
                root, filter, timeRange, leafField: dimFieldRef("cluster"), leafToVisible,
                groupDims, metrics, runQuery: runAnalyticsQuery, rowsToObjects,
            });
            if (!rows) return null;
            rows = rows.map((row) => ({ ...row, other: row.node }));
        } else {
            rows = rowsToObjects(await runAnalyticsQuery({
                root, timeRange, filter, limit: LEAF_ROLLUP_ROW_LIMIT,
                output: {
                    dimensions: [{ field: otherRef, outputAlias: "other" }, ...groupDims],
                    metrics: metrics.map(({ alias, predicate }) => promptCount(alias, "p", predicate)),
                },
            }));
            if (rows.length >= LEAF_ROLLUP_ROW_LIMIT) return null;
        }
        return rows.flatMap((row) => (group
            ? group.idsForValue(row.synth, chunk).map((synthId) => ({ synthId, otherId: row.other, count: Number(row.n) || 0 }))
            : chunk.map((synthId, index) => ({ synthId, otherId: row.other, count: Number(row[`id_${index}`]) || 0 }))
        ).filter((link) => link.count > 0));
    }));
    return results.some((rows) => rows === null) ? null : results.flat();
}

// Links between two synthetic columns in one read, when one side's predicate
// is row-local (a filtered count per id) and the other's can be grouped: the
// distinct prompts per (grouped value, id) are the intersections the per-id
// prompt sets gave, without their 10K cap. Returns [{ aId, bId, count }], or
// null for any other pairing (both sides tag the same joined table, so one
// read can't carry both), so the caller intersects per-id sets.
export async function pairFlowCounts(aAdapter, aIds, bAdapter, bIds, { range, baseScope = [] } = {}) {
    const rowLocalIsA = Boolean(aAdapter.predicateIsRowLocal && bAdapter.flowGroup);
    if (!rowLocalIsA && !(bAdapter.predicateIsRowLocal && aAdapter.flowGroup)) return null;
    const [rowLocal, rowLocalIds, grouped, groupedIds] = rowLocalIsA
        ? [aAdapter, aIds, bAdapter, bIds]
        : [bAdapter, bIds, aAdapter, aIds];
    if (!rowLocalIds.length || !groupedIds.length) return [];
    const extraDims = [...(rowLocal.predicateExtraDims ?? []), ...(grouped.predicateExtraDims ?? [])];
    const { root, filter: scopeFilter, unsupported = [] } = buildPromptRootAndFilter({ scopePath: baseScope, extraDims });
    if (unsupported.length) return [];
    const timeRange = rangeToTimeRange(toApiRange(range));
    const chunks = [];
    for (let start = 0; start < rowLocalIds.length; start += FLOW_METRICS_PER_QUERY) {
        chunks.push(rowLocalIds.slice(start, start + FLOW_METRICS_PER_QUERY));
    }
    const results = await Promise.all(chunks.map(async (chunk) => {
        const rows = rowsToObjects(await runAnalyticsQuery({
            root, timeRange, limit: LEAF_ROLLUP_ROW_LIMIT,
            filter: { andExpr: { children: [scopeFilter, rowLocal.predicate(chunk, "p"), grouped.predicate(groupedIds, "p")].filter(Boolean) } },
            output: {
                dimensions: [{ field: grouped.flowGroup.field, outputAlias: "synth" }],
                metrics: chunk.map((id, index) => promptCount(`id_${index}`, "p", rowLocal.predicate([id], "p"))),
            },
        }));
        if (rows.length >= LEAF_ROLLUP_ROW_LIMIT) return null;
        return rows.flatMap((row) => grouped.flowGroup.idsForValue(row.synth, groupedIds).flatMap((groupedId) => chunk
            .map((rowLocalId, index) => ({ rowLocalId, groupedId, count: Number(row[`id_${index}`]) || 0 }))
            .filter((link) => link.count > 0)));
    }));
    if (results.some((rows) => rows === null)) return null;
    return results.flat().map(({ rowLocalId, groupedId, count }) => (rowLocalIsA
        ? { aId: String(rowLocalId), bId: String(groupedId), count }
        : { aId: String(groupedId), bId: String(rowLocalId), count }));
}

// fetchPromptIds — used by synthetic × synthetic flow pairs. Returns
// the Set of logical prompt IDs matching this adapter's predicate for
// one id. The dispatcher intersects sets across sides in JS.
//
// Capped at 10K rows: predicates broader than that aren't meaningful
// as flow-link endpoints (the link reduces to "almost all prompts").
const PROMPT_ID_SET_CAP = 10000;

async function defaultFetchPromptIds(adapter, { id, range, baseScope = [] }) {
    const pred = adapter.predicate([id], "p");
    if (!pred) return new Set();
    const apiRange = toApiRange(range);
    // Joins needed for the spec come purely from the adapter's
    // predicate (no "other axis" here — fetchPromptIds is one-sided).
    const extraDims = adapter.predicateExtraDims ?? [];
    const { root, filter: scopeFilter, unsupported = [] } = buildPromptRootAndFilter({ scopePath: baseScope, extraDims });
    if (unsupported.length) return new Set();
    const spec = {
        root,
        timeRange: rangeToTimeRange(apiRange),
        filter: scopeFilter ? { andExpr: { children: [scopeFilter, pred] } } : pred,
        output: {
            dimensions: [{ field: { stepAlias: "p", field: "DIMENSION_FIELD_PROMPT_EVENT_ID" }, outputAlias: "prompt_id" }],
            // Group before the row limit so separate source captures do not
            // consume multiple slots for the same logical prompt.
            metrics: [promptCount("n")],
        },
        limit: PROMPT_ID_SET_CAP,
    };
    try {
        const rows = rowsToObjects(await runAnalyticsQuery(spec));
        return new Set(rows.map((r) => String(r.prompt_id)));
    } catch (err) {
        console.warn(`[flow] fetchPromptIds failed for ${adapter.id}=${id}:`, err);
        return new Set();
    }
}

// Session-level memo for fetchPromptIds. Same (adapter, id, range)
// tuple gets hit when two adjacent column views overlap; reusing the
// Promise avoids refetches. Bounded to 200 entries (LRU by insertion).
const _promptIdSetCache = new Map();
const PROMPT_ID_SET_CACHE_CAP = 200;
onAuthIdentityChange(() => _promptIdSetCache.clear());

function memoizedFetchPromptIds(adapter, args) {
    const epoch = getAuthIdentityEpoch();
    const key = `${epoch}|${adapter.id}|${args.id}|${JSON.stringify(args.range ?? null)}|${JSON.stringify(args.baseScope ?? [])}`;
    const hit = _promptIdSetCache.get(key);
    if (hit) return hit;
    const promise = defaultFetchPromptIds(adapter, args).then((ids) => (
        getAuthIdentityEpoch() === epoch ? ids : new Set()
    ));
    _promptIdSetCache.set(key, promise);
    if (_promptIdSetCache.size > PROMPT_ID_SET_CACHE_CAP) {
        const oldest = _promptIdSetCache.keys().next().value;
        _promptIdSetCache.delete(oldest);
    }
    return promise;
}

// Bind defaults onto any adapter that composes with the prompt graph
// and didn't supply its own. After this, dispatchers can call
// `adapter.fanOutForFlow(args)` / `adapter.fetchPromptIds(args)`
// without checking which adapters override what.
function bindFlowDefaults(adapter) {
    if (!adapter.composesWithPromptGraph) return adapter;
    if (!adapter.fanOutForFlow) {
        adapter.fanOutForFlow = (args) => defaultFanOutForFlow(adapter, args);
    }
    if (!adapter.fetchPromptIds) {
        adapter.fetchPromptIds = (args) => memoizedFetchPromptIds(adapter, args);
    }
    if (!adapter.flowCounts) {
        adapter.flowCounts = (args) => defaultFlowCounts(adapter, args);
    }
    return adapter;
}

bindFlowDefaults(mcpServerAdapter);
bindFlowDefaults(skillAdapter);
bindFlowDefaults(memoryAdapter);
// signalAdapter is composesWithPromptGraph: true — signals are an edge-joined
// graph dimension (PROMPT → SignalTags), so it gets the flow defaults bound like
// the other prompt-graph dims. (Identity used to be an adapter too; O-2400
// promoted it to a first-class graph dimension — see DIM_INFO.identity in
// analyticsClient.js.)
bindFlowDefaults(signalAdapter);

export const DIM_ADAPTERS = {
    [mcpServerAdapter.id]:  mcpServerAdapter,
    [skillAdapter.id]:      skillAdapter,
    [memoryAdapter.id]:     memoryAdapter,
    [signalAdapter.id]:     signalAdapter,
};

// ── Scope-step hydration notify ────────────────────────────────────
//
// Adapters with hydrateScopeStep populate their internal cache off-
// band; once a hydration completes, consumers of toApiScope need to
// re-resolve. We expose a single notify channel adapters call after
// updating their cache (e.g. inside their hydrateScopeStep
// implementation). useScopeHydration subscribes and bumps a state
// counter so the React tree re-renders and toApiScope picks up the
// fresh rewrite output.

const _hydrationSubscribers = new Set();

export function notifyScopeHydration() {
    for (const cb of _hydrationSubscribers) {
        try { cb(); } catch (err) { console.warn("[hydration] subscriber threw:", err); }
    }
}

export function onScopeHydration(cb) {
    _hydrationSubscribers.add(cb);
    return () => _hydrationSubscribers.delete(cb);
}

// ── Helpers used by toApiScope + useScopeHydration ────────────────

// Apply an adapter's rewriteScopeStep to a raw scope step. Returns:
//   - the array of replacement steps when the adapter rewrites
//   - the original step (unwrapped into a single-element array) when
//     the adapter has no rewriteScopeStep or returns null
//   - [] (empty) when the adapter explicitly returns [] — meaning
//     "not hydrated yet, drop the step from scope until I am"
export function rewriteScopeStepOrPassThrough(adapter, step, { range } = {}) {
    if (!adapter || typeof adapter.rewriteScopeStep !== "function") return [step];
    const rewritten = adapter.rewriteScopeStep(step, { range });
    if (rewritten == null) return [step];
    return rewritten;
}

// Walk a scope path and find any adapter steps that declare
// hydrateScopeStep — returns the list so a hydration driver can
// trigger them once per (id, range). Pure inspection; no side effects.
export function adapterStepsNeedingHydration(scopePath) {
    const out = [];
    for (const step of scopePath || []) {
        const adapter = getDimAdapter(step.dim) || getDimAdapterByPlural(step.dim);
        if (adapter && typeof adapter.hydrateScopeStep === "function") {
            out.push({ adapter, step });
        }
    }
    return out;
}

// True if `scopePath` contains an adapter step that declares
// hydrateScopeStep but whose resolution hasn't landed for this range yet.
// While true, rewriteScopeStep would drop the step and any scope-dependent
// query would run unscoped (the whole-tenant flash that then snaps down).
// The query hooks (useQuery / usePagedDimEntries) gate on this and hold in
// a loading state until the scope epoch bumps on hydration completion.
// Adapters opt in via isScopeStepHydrated; those without it are treated as
// always-ready, preserving the prior (flash-on-select) behavior.
export function scopePathAwaitingHydration(scopePath, range) {
    for (const step of scopePath || []) {
        const adapter = getDimAdapter(step.dim) || getDimAdapterByPlural(step.dim);
        if (!adapter || typeof adapter.hydrateScopeStep !== "function") continue;
        if (
            typeof adapter.isScopeStepHydrated === "function" &&
            !adapter.isScopeStepHydrated(step, { range })
        ) {
            return true;
        }
    }
    return false;
}

// Collect trace-detail chip data from every registered adapter that
// declares extractTraceFacets. Returns one group per adapter that
// returned at least one facet; ContextPanel renders each group as an
// eyebrow + chip row. Pure; no side effects; safe for useMemo.
export function extractAllTraceFacets(trace) {
    const out = [];
    for (const adapter of Object.values(DIM_ADAPTERS)) {
        if (typeof adapter.extractTraceFacets !== "function") continue;
        let facets;
        try {
            facets = adapter.extractTraceFacets(trace);
        } catch (err) {
            console.warn(`[traceFacets] ${adapter.id} threw:`, err);
            continue;
        }
        if (!Array.isArray(facets) || facets.length === 0) continue;
        out.push({
            adapterId:  adapter.id,
            groupLabel: adapter.traceFacetGroupLabel ?? adapter.label,
            facets,
        });
    }
    return out;
}

export function getDimAdapter(dim) {
    return DIM_ADAPTERS[dim] || null;
}

export function hasDimAdapter(dim) {
    return Object.prototype.hasOwnProperty.call(DIM_ADAPTERS, dim);
}

// Reverse-lookup by sidebar plural key ("mcp_servers", "skills" → adapter).
// Used by label resolution in dimensionDefs.js where dim keys are
// plural; the registry is keyed singular so adapters' .id matches
// DIM_INFO.
export function getDimAdapterByPlural(plural) {
    for (const adapter of Object.values(DIM_ADAPTERS)) {
        if (adapter.plural === plural) return adapter;
    }
    return null;
}

// shapeDimensionEntries — moved from apiClient so adapter modules can
// build their own envelopes without importing back from apiClient
// (cycle). Used by every listEntries pipeline (built-in or adapter).
//
// sparkline.series semantics:
//   Array — render the line as-is
//   []    — known-empty (show share bar only)
//   null  — "fetch lazily when the row scrolls into view" — the
//           sidebar runtime sees the sentinel and triggers a per-
//           entry getDimEntrySparkline request. Used by dims (today
//           mcp_server) where the bulk discovery query can't cheaply
//           carry the bucket fan-out.
export function shapeDimensionEntries({ entries = [], total = 0 } = {}) {
    const max = entries.reduce((m, e) => Math.max(m, Number(e.value) || 0), 0) || 1;
    const shaped = entries.map((e) => {
        const activity = Number(e.value) || 0;
        const rawShare = Math.round((activity / max) * 100);
        const share = activity > 0 ? Math.max(1, rawShare) : 0;
        const sparklineSeries = e.sparkline === null
            ? null
            : Array.isArray(e.sparkline) ? e.sparkline.map(Number) : [];
        return {
            id:    String(e.id ?? ""),
            label: String(e.label ?? e.id ?? ""),
            activity,
            share,
            // A per-entry secondary line (rendered muted under the label, two-row
            // layout). No shipped dim sets one today, but the sidebar runtime
            // supports it, so it's kept available.
            note: String(e.note ?? ""),
            // A short inline status tag on the row title — distinct from the
            // secondary-line `note`. The signals facet sets it to "disabled" so the
            // enabled/disabled grouping reads deliberately.
            badge: String(e.badge ?? ""),
            sparkline: {
                series: sparklineSeries,
                stroke: "#7fa16c",
                fill:   "rgba(127,161,108,0.12)",
            },
        };
    });
    return {
        entries: shaped,
        count:   shaped.length,
        total:   typeof total === "number" ? total : shaped.length,
        maxValue: max,
    };
}
